import { fetchActiveWeekInstoreTemplatesAsync } from "../features/instoreTemplates/instoreTemplatesSlice";
import { useAppDispatch, useAppSelector } from "../store/configureStore";
import { isWithinInterval, format } from "date-fns";
import { getTemplateDateWindow } from "../helpers/templateDateWindow";
import { useEffect } from "react";
import { InstoreTemplate } from "../models/instoreTemplate";

const STATIC_INSTORE_ORDER_TYPE_NAME = "Week";

// Returns the active "Week" instore templates whose date window covers today,
// one per instore type. There can be a separate Week template per instore type
// (= store_type); for each type we keep the most recent match (most recent
// `from`, tiebreak on most recent `created_at`). Multiple matches for the same
// type can occur with sloppy app usage.
export default function useActiveWeekInstoreTemplates(): InstoreTemplate[] {
  const dispatch = useAppDispatch();
  const { activeWeekTemplates, activeWeekTemplatesLoaded } = useAppSelector(
    (state) => state.instoreTemplates
  );

  useEffect(() => {
    if (!activeWeekTemplatesLoaded) {
      dispatch(
        fetchActiveWeekInstoreTemplatesAsync(format(new Date(), "yyyy-MM-dd"))
      );
    }
  }, [activeWeekTemplatesLoaded, dispatch]);

  type Match = {
    template: InstoreTemplate;
    fromTime: number;
    createdAtTime: number;
  };

  const today = new Date();
  const matches = activeWeekTemplates
    .map<Match | null>((template) => {
      if (!template.active) return null;
      if (template.order_type_instore_name !== STATIC_INSTORE_ORDER_TYPE_NAME)
        return null;
      // `until` is made inclusive (end of that day) by the helper — see there.
      const window = getTemplateDateWindow(template);
      if (!window) return null;
      if (!isWithinInterval(today, { start: window.from, end: window.until }))
        return null;
      const createdAt = template.created_at
        ? new Date(template.created_at).getTime()
        : 0;
      return {
        template,
        fromTime: window.from.getTime(),
        createdAtTime: isNaN(createdAt) ? 0 : createdAt,
      };
    })
    .filter((m): m is Match => m !== null);

  matches.sort((a, b) => {
    if (b.fromTime !== a.fromTime) return b.fromTime - a.fromTime;
    return b.createdAtTime - a.createdAtTime;
  });

  // Keep only the most recent match per instore type. `matches` is already
  // sorted most-recent-first, so the first one seen per type wins.
  const byType = new Map<number, InstoreTemplate>();
  for (const match of matches) {
    if (!byType.has(match.template.instore_type_id)) {
      byType.set(match.template.instore_type_id, match.template);
    }
  }

  return Array.from(byType.values());
}
